import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useData } from '../context/DataContext';
import VehicleCard from '../components/VehicleCard';

const { FiArrowRight, FiShield, FiUsers, FiAward } = FiIcons;

const HomePage = () => {
  const { vehicles, loading } = useData();
  
  // Only show active vehicles on home
  const featuredVehicles = vehicles
    .filter(v => v.status === 'active')
    .slice(0, 3);

  const features = [
    {
      icon: FiShield,
      title: 'Calidad certificada',
      description: 'Rigurosa inspección de 50+ puntos en todos los vehículos antes de la venta'
    },
    {
      icon: FiUsers,
      title: 'Atención personalizada',
      description: 'Te ayudamos a encontrar el vehículo que mejor se adapte a tus necesidades'
    },
    {
      icon: FiAward,
      title: 'Transparencia total',
      description: 'Historial completo, documentación al día y toda la información necesaria'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#005c91] to-[#3498db] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920&h=1080&fit=crop&crop=center" 
            alt="Cars" 
            className="w-full h-full object-cover opacity-20" 
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Tu próximo <span className="block text-[#e67e22]">coche te espera</span>
            </h1>
            <p className="text-xl mb-8 text-blue-100">
              Descubre nuestra selección de vehículos de segunda mano con garantía y calidad certificada en Cáceres.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/catalogo" 
                className="bg-[#e67e22] hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors inline-flex items-center justify-center"
              >
                Ver catálogo
                <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <a 
                href="https://wa.me/34610701517"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-white text-white hover:bg-white hover:text-[#005c91] px-8 py-4 rounded-full font-semibold text-lg transition-colors inline-flex items-center justify-center"
              >
                Contactar WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white p-8 rounded-xl shadow-lg text-center"
              >
                <div className="w-16 h-16 bg-[#005c91] rounded-full flex items-center justify-center mb-6 mx-auto">
                  <SafeIcon icon={feature.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#2c3e50] mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Vehicles Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#2c3e50] mb-4">Vehículos Destacados</h2>
            <p className="text-xl text-gray-600">Nuestras mejores ofertas seleccionadas para ti</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              // Loading Skeletons - Show while loading
              [1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-xl shadow-lg overflow-hidden h-96 animate-pulse">
                  <div className="bg-gray-200 h-48 w-full"></div>
                  <div className="p-6 space-y-4">
                    <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="h-4 bg-gray-200 rounded"></div>
                      <div className="h-4 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </div>
              ))
            ) : featuredVehicles.length > 0 ? (
              // Show vehicles if they exist and finished loading
              featuredVehicles.map((vehicle, index) => (
                <motion.div 
                  key={vehicle.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <VehicleCard vehicle={vehicle} />
                </motion.div>
              ))
            ) : (
              // Only show this if loading is FALSE and there are NO vehicles
              <div className="col-span-full text-center py-20 bg-gray-50 rounded-xl">
                <p className="text-gray-500 text-lg">No hay vehículos destacados en este momento.</p>
                <Link to="/catalogo" className="text-[#005c91] font-medium mt-2 inline-block">Ver catálogo completo</Link>
              </div>
            )}
          </div>

          {!loading && featuredVehicles.length > 0 && (
            <div className="text-center mt-12">
              <Link 
                to="/catalogo" 
                className="bg-[#005c91] hover:bg-[#3498db] text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors inline-flex items-center"
              >
                Ver todos los vehículos
                <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomePage;